import { useEffect, useState } from 'react';
import { useAuth } from '@chainlit/react-client';
import { Navigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Loader2, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';

import { adminApi, type User } from '@/api/admin';
import { orgApi, type Org } from '@/api/organizations';
import { PASSWORD_POLICY_TEXT, passwordPolicyError } from '@/lib/password';

const ROLE_LABEL: Record<string, string> = {
  super_admin: '系統管理員',
  org_user:    '業者使用者',
  org_viewer:  '業者訪客',
};

const ROLE_BADGE_CLASS: Record<string, string> = {
  super_admin: 'bg-primary/20 text-primary border-primary/30',
  org_user:    'bg-blue-500/20 text-blue-600 border-blue-500/30 dark:text-blue-400',
  org_viewer:  'bg-gray-500/20 text-gray-600 border-gray-500/30 dark:text-gray-400',
};

export default function AdminPage() {
  const { user: me } = useAuth();
  const myRole = (me?.metadata as Record<string, unknown>)?.role as string | undefined;

  const [users, setUsers] = useState<User[]>([]);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ username: '', password: '', role: 'org_user', org_id: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ role: '', password: '', org_id: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Guard: only super_admin can access
  if (myRole !== 'super_admin') {
    return <Navigate replace to="/chat" />;
  }

  const fetchUsers = () => {
    setLoading(true);
    Promise.all([adminApi.list(), orgApi.list()])
      .then(([u, o]) => { setUsers(u); setOrgs(o); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  // ── Create ──
  const handleCreate = async () => {
    setCreateError('');
    if (!createForm.username.trim() || !createForm.password) {
      setCreateError('使用者名稱和密碼不可為空');
      return;
    }
    const pwError = passwordPolicyError(createForm.password);
    if (pwError) {
      setCreateError(pwError);
      return;
    }
    setCreateLoading(true);
    try {
      const newUser = await adminApi.create(
        createForm.username.trim(), createForm.password, createForm.role,
        createForm.org_id || null,
      );
      const selectedOrg = orgs.find((o) => o.id === createForm.org_id);
      setUsers((prev) => [...prev, { ...newUser, org_name: selectedOrg?.name ?? null }]);
      setCreateOpen(false);
      setCreateForm({ username: '', password: '', role: 'org_user', org_id: '' });
    } catch (e: unknown) {
      setCreateError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Edit ──
  const openEdit = (u: User) => {
    setEditTarget(u);
    setEditForm({ role: u.role, password: '', org_id: u.org_id || '' });
    setEditError('');
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    setEditError('');
    const data: { role?: string; password?: string; org_id?: string | null } = {};
    if (editForm.role !== editTarget.role) data.role = editForm.role;
    if (editForm.password) {
      const pwError = passwordPolicyError(editForm.password);
      if (pwError) {
        setEditError(pwError);
        return;
      }
      data.password = editForm.password;
    }
    const newOrgId = editForm.org_id || null;
    if (newOrgId !== editTarget.org_id) data.org_id = newOrgId;
    if (!data.role && !data.password && data.org_id === undefined) {
      setEditOpen(false);
      return;
    }
    setEditLoading(true);
    try {
      const updated = await adminApi.update(editTarget.id, data);
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      setEditOpen(false);
    } catch (e: unknown) {
      setEditError(e instanceof Error ? e.message : String(e));
    } finally {
      setEditLoading(false);
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await adminApi.delete(deleteTarget.id);
      setUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id));
      setDeleteOpen(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setDeleteOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">使用者管理</h2>
        <Button
          variant="outline"
          className="border-primary text-primary hover:bg-primary/10"
          onClick={() => { setCreateError(''); setCreateForm({ username: '', password: '', role: 'user' }); setCreateOpen(true); }}
        >
          <Plus className="size-4 mr-1" />
          新增使用者
        </Button>
      </div>

      {/* Loading / Error */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
          <Loader2 className="size-5 animate-spin" />
          <span>載入中…</span>
        </div>
      )}
      {!loading && error && (
        <div className="text-center py-8 text-red-500 text-sm">{error}</div>
      )}

      {/* User table */}
      {!loading && !error && (
        users.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="size-12 mx-auto mb-3 opacity-40" />
            <p>尚無使用者</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>使用者名稱</TableHead>
                  <TableHead>角色</TableHead>
                  <TableHead>業者</TableHead>
                  <TableHead>建立時間</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.username}</TableCell>
                    <TableCell>
                      <Badge className={`${ROLE_BADGE_CLASS[u.role] || ''} hover:bg-opacity-20 text-xs font-medium`}>
                        {ROLE_LABEL[u.role] || u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {u.org_name || '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(u.created_at).toLocaleString('zh-TW')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(u)}>
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          disabled={u.id === (me as { id?: string })?.id}
                          onClick={() => { setDeleteTarget(u); setDeleteOpen(true); }}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      )}

      {/* ── Create Dialog ── */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新增使用者</DialogTitle>
            <DialogDescription>建立新的使用者帳號</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">使用者名稱</label>
              <Input
                value={createForm.username}
                onChange={(e) => setCreateForm((f) => ({ ...f, username: e.target.value }))}
                placeholder="輸入使用者名稱"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">密碼</label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="輸入密碼"
              />
              <p className="text-xs text-muted-foreground">{PASSWORD_POLICY_TEXT}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">角色</label>
              <Select value={createForm.role} onValueChange={(v) => setCreateForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">系統管理員</SelectItem>
                  <SelectItem value="org_user">業者使用者</SelectItem>
                  <SelectItem value="org_viewer">業者訪客</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">業者（選填）</label>
              <Select value={createForm.org_id} onValueChange={(v) => setCreateForm((f) => ({ ...f, org_id: v === '__none__' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="不指定業者" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">不指定</SelectItem>
                  {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {createError && <p className="text-sm text-red-500">{createError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>取消</Button>
            <Button onClick={handleCreate} disabled={createLoading}>
              {createLoading && <Loader2 className="size-4 mr-1 animate-spin" />}
              建立
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Dialog ── */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>編輯使用者</DialogTitle>
            <DialogDescription>修改 {editTarget?.username} 的角色或密碼</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">角色</label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">系統管理員</SelectItem>
                  <SelectItem value="org_user">業者使用者</SelectItem>
                  <SelectItem value="org_viewer">業者訪客</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">業者</label>
              <Select value={editForm.org_id || '__none__'} onValueChange={(v) => setEditForm((f) => ({ ...f, org_id: v === '__none__' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="不指定業者" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">不指定</SelectItem>
                  {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">新密碼（留空則不變更）</label>
              <Input
                type="password"
                value={editForm.password}
                onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="輸入新密碼"
              />
              <p className="text-xs text-muted-foreground">{PASSWORD_POLICY_TEXT}</p>
            </div>
            {editError && <p className="text-sm text-red-500">{editError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>取消</Button>
            <Button onClick={handleEdit} disabled={editLoading}>
              {editLoading && <Loader2 className="size-4 mr-1 animate-spin" />}
              儲存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete AlertDialog ── */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確認刪除</AlertDialogTitle>
            <AlertDialogDescription>
              確定要刪除使用者「{deleteTarget?.username}」嗎？此操作無法復原。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteLoading && <Loader2 className="size-4 mr-1 animate-spin" />}
              刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
